import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Redux
import { setOrderType } from 'store/Client/ClientAnalyser';
import {
  fetchClientOrderDisplay,
  fetchClientTextOrderDisplay,
} from 'store/Client/OrderDisplay/features';
import { handleSorting } from 'store/Client/ClientOrderList';

// Hooks & utils
import { useHistoryState } from 'utils/hooks/useHistoryState';
import {
  FILTER_ORDER_TYPE_LIST,
  ORDER_TYPE,
  pageNbOptions,
} from 'utils/Constants';
import { filterOrders } from 'helpers/FilterOrders';
import { usePagination } from 'utils/hooks/usePagination';
import { useDeadlineFilter } from 'utils/hooks/useDeadlineFilter';
import { formatDate } from 'utils/DateUtils';
import { exportRoadMap } from 'helpers/ExportRoadmapHelpers';

// Components
import { ChevronDownIcon } from 'icons/ChevronIcon';
import { Loader } from 'components/shared/Loader';
import ErrorComponent from 'components/shared/ErrorComponent';
import OrderTypeFilter from 'components/shared/Filter/OrderTypeFilter';
import CheckBox from 'components/shared/CheckBox';
import { CustomTooltip } from 'components/shared/CustomTooltip';
import { ProgressBar } from 'components/shared/ProgressBar';
import { Paginator } from 'components/shared/Paginator';
import SearchIcon from 'icons/SearchIcon';
import { CircularBar } from 'components/shared/CircularBar';
import DeadlineFilterPopover from 'components/shared/Filter/DeadlineFilterPopover';
import DataTable from 'components/shared/DataTable';

const getColumns = ({
  t,
  selectedYear,
  setSelectedYear,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedType,
  setSelectedType,
  handleTypeClick,
  handleTypeClose,
  anchorEl,
  selectedOrders,
  uniqueOrderCount,
  onAllSelectedChange,
  onOrderSelectedChange,
  exportAllOrdersRoadMap,
  user,
}) => [
  {
    header: t('Titre de la commande'),
    accessor: 'title',
    sortable: true,
    width: '26%',
    renderCell: (order) => (
      <div style={{ display: 'flex' }}>
        <div style={{ width: '350px' }}>{order?.title}</div>
        {order.isNew && (
          <button
            className="bt-submit-table"
            style={{
              background: '#43B929',
              color: 'white',
              height: '18px',
              marginLeft: '10px',
              fontSize: '14px',
            }}
          >
            New
          </button>
        )}
      </div>
    ),
  },
  {
    header: t('Type'),
    accessor: 'typeName',
    sortable: false,
    width: '15%',
    filter: (
      <OrderTypeFilter
        orderTypeList={FILTER_ORDER_TYPE_LIST}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        handleClick={handleTypeClick}
        handleClose={handleTypeClose}
        anchorEl={anchorEl}
      />
    ),
  },
  {
    header: t('Deadline'),
    accessor: 'deadline',
    sortable: true,
    width: '20%',
    filter: (
      <DeadlineFilterPopover
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
    ),
  },
  {
    header: t('Textes livrés'),
    accessor: 'deliveredTextsAmount',
    sortable: true,
    renderCell: (o) => (
      <ProgressBar amount={o.deliveredTextsAmount} total={o.textAmount} />
    ),
  },
  {
    header: t('Textes validés'),
    accessor: 'validatedTextsAmount',
    sortable: true,
    renderCell: (o) => (
      <ProgressBar amount={o.validatedTextsAmount} total={o.textAmount} />
    ),
  },
  {
    header: t('Textes en ligne'),
    accessor: 'deployedTextsAmount',
    sortable: true,
    renderCell: (o) => (
      <ProgressBar amount={o.deployedTextsAmount} total={o.textAmount} />
    ),
  },
  {
    header: t('Temps restant'),
    accessor: 'finishedAt',
    sortable: true,
    renderCell: (o) => {
      if (!o.areAllTextValidated) {
        return <CircularBar finishedAt={o.finishedAt} orderId={o.id} />;
      }

      return null;
    },
  },
  {
    header: '',
    accessor: 'selected',
    sortable: false,
    renderHeader: () => (
      <CheckBox
        name="select-all"
        tick={
          selectedOrders.size > 0 && selectedOrders.size === uniqueOrderCount
        }
        onCheck={(e) => onAllSelectedChange(e.target.checked)}
      />
    ),
    renderCell: (order) => (
      <CheckBox
        key={order.id}
        name={order.title}
        tick={selectedOrders.has(order.id)}
        onCheck={(e) => onOrderSelectedChange(order.id, e.target.checked)}
      />
    ),
  },
  {
    header: '',
    accessor: 'downloadAll',
    sortable: false,
    renderHeader: () => (
      <div style={{ position: 'relative' }}>
        <div className="d-flex justify-content-center">
          <span
            className={
              selectedOrders.size > 1
                ? 'bt-download-circle-selected'
                : 'bt-download-circle'
            }
            onClick={() => exportAllOrdersRoadMap()}
          ></span>
        </div>
        <CustomTooltip
          title={t(
            "Vous avez la possibilité d'exporter le rapport d'une, ou de plusieurs commandes. Pour exporter toutes les commandes d'un client, il vous suffit juste de cliquer sur l'icône de téléchargement en jaune"
          )}
          placement="top"
        >
          <button
            type="button"
            className="btn-circle"
            style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
            }}
          ></button>
        </CustomTooltip>
      </div>
    ),
    renderCell: (order) => (
      <div className="d-flex justify-content-center">
        <span
          className="bt-download-circle"
          onClick={() => exportRoadMap(null, order.id, user.account, t)}
        ></span>
      </div>
    ),
  },
];

export const ClientOrdersTable = ({
  orderStatus,
  orders,
  isLoading,
  isError,
  setShowDownloadModel,
  user,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const uniqueOrderCount = useMemo(
    () => new Set(orders.map((o) => o.id)).size,
    [orders]
  );

  // Local state for filters, sorting, pagination
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [selectedType, setSelectedType] = useHistoryState(
    'selectedFilter',
    FILTER_ORDER_TYPE_LIST[0]
  );
  const [titleSearch, setTitleSearch] = useHistoryState('titleSearch', '');
  const [anchorEl, setAnchorEl] = useState(null);
  const [titleFilteredOrders, setTitleFilteredOrders] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Local state for managing selected orders
  const [selectedOrders, setSelectedOrders] = useState(new Set());

  // 1) Filter by order type & search text
  useEffect(() => {
    if (!orders) return;
    const filtered = filterOrders(orders, selectedType, titleSearch);
    if (titleSearch.length > 0) setCurrentPage(1);
    setTitleFilteredOrders(filtered);
  }, [orders, selectedType, titleSearch]);

  // 2) Deadline filtering (year / date range)
  const {
    filteredOrders,
    selectedYear,
    setSelectedYear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useDeadlineFilter(titleFilteredOrders);

  // 3) Pagination for filtered orders
  const { currentPage, setCurrentPage, totalPages, paginatedItems } =
    usePagination(filteredOrders, ordersPerPage);

  // 4) Prepare rows, injecting display fields
  const rows = paginatedItems.map((order) => ({
    ...order,
    title: order.title,
    typeName: ORDER_TYPE.find((o) => o.type === order.type)?.name,
    deadline: order.deadline ? formatDate(order.deadline) : null,
  }));

  // 5) Dropdown & row-click handlers
  const handleTypeClick = (idx, e) => setAnchorEl({ [idx]: e.currentTarget });
  const handleTypeClose = () => setAnchorEl(null);

  const handleRowClick = (order) => {
    console.log('🚀 ~ handleRowClick ~ order:', order.type);

    dispatch(setOrderType(order.type));
    dispatch(fetchClientOrderDisplay(order.id));
    dispatch(fetchClientTextOrderDisplay(order.id));
    history.push('/Client/DetailsDeLaCommande');
  };
  // 6) Get columns
  const columns = getColumns({
    t,
    selectedYear,
    setSelectedYear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedType,
    setSelectedType,
    handleTypeClick,
    handleTypeClose,
    anchorEl,
    selectedOrders,
    uniqueOrderCount,
    onAllSelectedChange,
    onOrderSelectedChange,
    exportAllOrdersRoadMap,
    user,
  });

  // 7) Sorting handler
  const handleSortingChange = (field) => {
    const direction =
      field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    dispatch(
      handleSorting({
        table: orderStatus,
        sortField: field,
        sortOrder: direction,
      })
    );
    setSortField(field);
    setSortOrder(direction);
  };

  // 8) Order selection handlers
  function onAllSelectedChange(isChecked) {
    const newSet = isChecked
      ? new Set(orders.map((order) => order.id))
      : new Set();
    setSelectedOrders(newSet);
  }

  function onOrderSelectedChange(orderId, isChecked) {
    const newSet = new Set(selectedOrders);
    isChecked ? newSet.add(orderId) : newSet.delete(orderId);
    setSelectedOrders(newSet);
  }

  // 9) export orders
  function exportAllOrdersRoadMap() {
    setShowDownloadModel(true);

    // Determine IDs to export
    const idsToExport =
      selectedOrders.size > 0
        ? selectedOrders
        : new Set(orders.map((order) => order.id));

    exportRoadMap(idsToExport, null, user?.account, t).finally(() =>
      setShowDownloadModel(false)
    );
  }

  return (
    <div>
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <ErrorComponent />
      ) : (
        <>
          {/* Toolbar */}
          <div className="box-info-header">
            <div className="b-child-2">
              <div className="d-flex">
                <div className="box-input-text pe-0">
                  <div className="input-group input-cs w-100 pe-0">
                    <div className="input-group-text">
                      <SearchIcon />
                    </div>
                    <input
                      type="text"
                      className="form-control ps-0"
                      value={titleSearch}
                      onChange={(event) => setTitleSearch(event.target.value)}
                      placeholder={t('Rechercher')}
                    />
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <span className="me-2"> {t('Afficher les résultats')}</span>

                  <div className="dropdown" style={{ width: '70px' }}>
                    <button
                      className="btn dropdown-toggle dropdown-custom w-100"
                      type="button"
                      id="dropdownMenuButton1"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {ordersPerPage}
                      <span>
                        <ChevronDownIcon />
                      </span>
                    </button>
                    <ul
                      className="dropdown-menu"
                      aria-labelledby="dropdownMenuButton1"
                    >
                      {pageNbOptions
                        .filter((nb) => nb !== ordersPerPage)
                        .map((item, index) => (
                          <li
                            key={index}
                            onClick={() => setOrdersPerPage(item)}
                          >
                            <button className="dropdown-item">{item}</button>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <DataTable
            columns={columns}
            data={rows}
            enableSorting
            onRowClick={handleRowClick}
            onSort={handleSortingChange}
            sortField={sortField}
            sortOrder={sortOrder}
          />

          {/* Paginator */}
          <Paginator
            isLoading={isLoading}
            listLength={filteredOrders.length}
            currentPage={currentPage}
            pageNumbers={[...Array(totalPages).keys()].map((i) => i + 1)}
            nbElementsPerPage={ordersPerPage}
            goToPage={setCurrentPage}
          />
        </>
      )}
    </div>
  );
};
